import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Loading, ErrorState } from "@/components/common/States";
import EmptyState from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { CheckCircle2, Clock, AlertTriangle, CalendarDays, CalendarClock } from "lucide-react";

const BUCKETS = [
  { key: "overdue", label: "Overdue", icon: AlertTriangle, tone: "text-rose-600 bg-rose-50 border-rose-200" },
  { key: "today", label: "Due today", icon: Clock, tone: "text-amber-600 bg-amber-50 border-amber-200" },
  { key: "tomorrow", label: "Tomorrow", icon: CalendarDays, tone: "text-sky-600 bg-sky-50 border-sky-200" },
  { key: "upcoming", label: "Upcoming", icon: CalendarClock, tone: "text-slate-600 bg-slate-50 border-slate-200" },
];

export default function FollowupsPanel({ salesperson, currentUserId, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try { const { data } = await api.get("/activities/followups", { params: salesperson ? { salesperson } : {} }); setData(data); }
    catch (e) { setError(apiError(e)); }
  }, [salesperson]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const complete = async (tid) => {
    try { await api.post(`/tasks/${tid}/complete`); toast.success("Marked done"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Loading follow-ups…" />;

  return (
    <div data-testid="followups-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {BUCKETS.map((b) => (
          <div key={b.key} className={`rounded-xl border p-4 ${b.tone}`} data-testid={`bucket-count-${b.key}`}>
            <b.icon className="h-5 w-5 mb-2" />
            <div className="text-2xl font-bold">{data.counts[b.key]}</div>
            <div className="text-xs font-medium">{b.label}</div>
          </div>
        ))}
      </div>
      <div className="space-y-6">
        {BUCKETS.map((b) => {
          const items = data.buckets[b.key];
          if (!items?.length) return null;
          return (
            <div key={b.key}>
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><b.icon className="h-4 w-4" /> {b.label} ({items.length})</h3>
              <div className="space-y-2">
                {items.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg p-3" data-testid={`task-${t.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 truncate">{t.title}</div>
                      <div className="text-xs text-slate-400 truncate">{t.type} · due {fmtDate(t.due_date)}{t.lead_name ? ` · ${t.lead_name}` : ""}{t.user_name && t.user_id !== currentUserId ? ` · ${t.user_name}` : ""}</div>
                    </div>
                    {t.user_id === currentUserId && <Button size="sm" variant="outline" onClick={() => complete(t.id)} data-testid={`complete-task-${t.id}`}><CheckCircle2 className="h-4 w-4" /> Done</Button>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {Object.values(data.counts).every((c) => c === 0) && <EmptyState title="No follow-ups" description="You're all caught up. New follow-ups will appear here." icon={CheckCircle2} />}
      </div>
    </div>
  );
}
